/**
 * Dashboard Service
 *
 * Provides dashboard-related data fetching.
 * Currently returns mock/placeholder data until API is connected.
 */

export interface DashboardStats {
  totalScans: number;
  averageScore: number | null;
  worksheetsUsed: number;
  scansThisWeek: number;
}

export interface RecentScan {
  id: string;
  worksheetName: string;
  studentName: string | null;
  score: number;
  totalQuestions: number;
  scannedAt: string;
}

/**
 * Get dashboard statistics for the current user.
 *
 * @param signal - AbortController signal for request cancellation
 * @returns Dashboard stats (mock data for now)
 */
export async function getDashboardStats(signal?: AbortSignal): Promise<DashboardStats> {
  // Simulate network delay
  await new Promise((resolve, reject) => {
    const timeout = setTimeout(resolve, 300);
    signal?.addEventListener('abort', () => {
      clearTimeout(timeout);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });

  // Return mock data - will be replaced with API call
  return {
    totalScans: 0,
    averageScore: null,
    worksheetsUsed: 0,
    scansThisWeek: 0,
  };
}

/**
 * Get recent scans for the current user.
 *
 * @param limit - Maximum number of scans to return
 * @param signal - AbortController signal for request cancellation
 * @returns Array of recent scans (empty for now)
 */
export async function getRecentScans(limit: number = 5, signal?: AbortSignal): Promise<RecentScan[]> {
  // Simulate network delay
  await new Promise((resolve, reject) => {
    const timeout = setTimeout(resolve, 200);
    signal?.addEventListener('abort', () => {
      clearTimeout(timeout);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });

  // Return empty array - will be replaced with API call
  return [];
}

/**
 * Check if an error is a request cancellation.
 */
export function isRequestCancelled(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}
